import { formatContractError } from "../../../utils/errors";
import {
  NoWalletProviderError,
  isWrongNetworkError,
} from "../../../utils/wallet/activeSigner";

const ORDER_REJECTED = "OrderRejectedError";

/**
 * An order the app stopped, or the exchange refused, with a sentence already
 * written for the trader (an unreadable book, a relayer rejection, a lock the
 * risk engine would refuse). It must reach the UI word for word: the revert
 * formatter would otherwise cut anything past 200 characters.
 *
 * Recognised by `name`, not `instanceof`: a subclass of `Error` loses its
 * prototype when transpiled to ES5.
 */
export const orderRejected = (message: string): Error => {
  const error = new Error(message);
  error.name = ORDER_REJECTED;
  return error;
};

/**
 * Errors the app raised itself, already worded for the trader and carrying a
 * `name` the UI branches on. Re-wrapping a `WrongNetworkError` in a plain
 * `Error` destroyed both: `isWrongNetworkError` stopped matching (so the Switch
 * Network dialog never rendered) and the revert formatter truncated its text.
 */
export const isOwnOrderError = (error: unknown): boolean =>
  isWrongNetworkError(error) ||
  error instanceof NoWalletProviderError ||
  // The wallet the trader chose is no longer attached. Raised by
  // getActiveEip1193() instead of silently signing through whichever extension
  // won the window.ethereum race, and it already names the wallet to reconnect --
  // which is precisely the wording the re-wrap above would destroy.
  //
  // By `name`, which survives a duplicated class across a split bundle -- the
  // same reason isWrongNetworkError carries a name fallback. NOT for the ES5
  // prototype loss this file's older comment cites: tsconfig targets es2020 and
  // browserslist resolves to nothing older than chrome 103, so `instanceof` on an
  // Error subclass works in this build. (Its sibling NoWalletProviderError uses
  // `instanceof` and is fine for that reason.)
  (error as { name?: unknown } | null)?.name ===
    "SelectedWalletUnavailableError" ||
  (error as { name?: unknown } | null)?.name === ORDER_REJECTED;

/**
 * An order-path failure as something a trader can read: a contract revert is
 * decoded (custom-error name, known selector, readable args), anything else
 * keeps its message minus ethers' metadata. Shared by spot and perp so the same
 * revert reads the same on both.
 */
export const toOrderTransactionError = (
  error: any,
  fallback: string,
  decodeExtra?: (error: any) => string | undefined
): Error => {
  if (isOwnOrderError(error)) return error;
  return new Error(
    formatContractError(error) || decodeExtra?.(error) || error?.message || fallback
  );
};
